/**
 * AnalysisDialog Component
 * Dialog for creating new analyses with form fields
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { X, Loader2, Check, Star, ChevronDown } from 'lucide-react';
import {
  GlassModal,
  GlassModalContent,
  GlassModalHeader,
  GlassModalTitle,
  GlassModalDescription,
} from '@/components/common/GlassModal/GlassModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmCloseDialog } from '../ConfirmCloseDialog/ConfirmCloseDialog';
import { cn } from '@/lib/utils';
import { vertriebsgebietService } from '@/services/api/vertriebsgebiet';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import { AppError } from '@/types/errors';

/** Form field values */
export interface AnalysisFormData {
  /** Sales region - validated via backend LLM */
  vertriebsgebiet: string;
  /** Target group selection */
  zielgruppe: 'privatkunden' | 'geschaeftskunden' | 'andere';
  /** Custom target group (when "andere" is selected) */
  zielgruppeCustom?: string;
  /** Industry */
  branche: string;
  /** Sales goal */
  vertriebsziel: 'neukunden' | 'bestandskunden' | 'rueckgewinnung';
  /** Optional details about the sales goal */
  vertriebszielDetails?: string;
  /** Product link URL */
  produktlink: string;
}

export interface AnalysisDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Submit handler */
  onSubmit?: (data: AnalysisFormData) => void | Promise<void>;
  /** Whether submission is in progress */
  isSubmitting?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Analysis dialog with form fields for creating new analyses
 */
export function AnalysisDialog({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  className,
}: AnalysisDialogProps) {
  const { t } = useTranslation();
  const { trackAnalysis } = useFeatureTracking();
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [catalogExpanded, setCatalogExpanded] = useState(false);

  // Vertriebsgebiet validation state
  const [vertriebsgebietInput, setVertriebsgebietInput] = useState('');
  const [vertriebsgebietSuggestions, setVertriebsgebietSuggestions] = useState<string[]>([]);
  const [isValidatingVertriebsgebiet, setIsValidatingVertriebsgebiet] = useState(false);
  const [vertriebsgebietValidationError, setVertriebsgebietValidationError] = useState<string | null>(null);
  const [vertriebsgebietConfirmed, setVertriebsgebietConfirmed] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create validation schema with translations
  const createSchema = () =>
    z.object({
      vertriebsgebiet: z
        .string()
        .min(1, t('validation.fieldRequired', { field: t('analysisDialog.vertriebsgebiet') })),
      zielgruppe: z.enum(['privatkunden', 'geschaeftskunden', 'andere'], {
        error: t('validation.fieldRequired', { field: t('analysisDialog.zielgruppe') }),
      }),
      zielgruppeCustom: z.string().optional(),
      branche: z
        .string()
        .min(1, t('validation.fieldRequired', { field: t('analysisDialog.branche') })),
      vertriebsziel: z.enum(['neukunden', 'bestandskunden', 'rueckgewinnung'], {
        error: t('validation.fieldRequired', { field: t('analysisDialog.vertriebsziel') }),
      }),
      vertriebszielDetails: z.string().optional(),
      produktlink: z
        .string()
        .min(1, t('validation.fieldRequired', { field: t('analysisDialog.produktlink') })),
    }).refine(
      (data) => {
        if (data.zielgruppe === 'andere') {
          return data.zielgruppeCustom && data.zielgruppeCustom.trim().length > 0;
        }
        return true;
      },
      {
        message: t('validation.fieldRequired', { field: t('analysisDialog.zielgruppeCustomLabel') }),
        path: ['zielgruppeCustom'],
      }
    );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<AnalysisFormData>({
    resolver: zodResolver(createSchema()),
    defaultValues: {
      vertriebsgebiet: '',
      zielgruppe: undefined,
      zielgruppeCustom: '',
      branche: '',
      vertriebsziel: 'neukunden',
      vertriebszielDetails: '',
      produktlink: '',
    },
  });

  const watchZielgruppe = watch('zielgruppe');

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      reset();
      setShowConfirmClose(false);
      setVertriebsgebietInput('');
      setVertriebsgebietSuggestions([]);
      setIsValidatingVertriebsgebiet(false);
      setVertriebsgebietValidationError(null);
      setVertriebsgebietConfirmed(false);
      setCatalogExpanded(false);
    }
  }, [isOpen, reset]);

  // Debounced backend validation for Vertriebsgebiet
  const validateVertriebsgebiet = useCallback(async (input: string) => {
    if (input.trim().length < 2) {
      setVertriebsgebietSuggestions([]);
      setVertriebsgebietValidationError(null);
      return;
    }

    setIsValidatingVertriebsgebiet(true);
    setVertriebsgebietValidationError(null);
    setVertriebsgebietConfirmed(false);
    setValue('vertriebsgebiet', '', { shouldDirty: true });

    try {
      const result = await vertriebsgebietService.validate(input);
      setVertriebsgebietSuggestions(result.suggestions);
      if (result.suggestions.length === 0) {
        setVertriebsgebietValidationError(t('analysisDialog.vertriebsgebietNoResults'));
      }
    } catch (error) {
      if (error instanceof AppError && error.code === ('RATE_LIMITED' as string)) {
        setVertriebsgebietValidationError(t('analysisDialog.vertriebsgebietRateLimited'));
      } else {
        setVertriebsgebietValidationError(t('analysisDialog.vertriebsgebietValidationError'));
      }
    } finally {
      setIsValidatingVertriebsgebiet(false);
    }
  }, [setValue, t]);

  const handleVertriebsgebietInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVertriebsgebietInput(value);
    setVertriebsgebietConfirmed(false);
    setValue('vertriebsgebiet', '', { shouldDirty: true });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      validateVertriebsgebiet(value);
    }, 500);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setVertriebsgebietInput(suggestion);
    setValue('vertriebsgebiet', suggestion, { shouldValidate: true, shouldDirty: true });
    setVertriebsgebietSuggestions([]);
    setVertriebsgebietConfirmed(true);
    setVertriebsgebietValidationError(null);
  };

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFormSubmit = async (data: AnalysisFormData) => {
    if (!vertriebsgebietConfirmed) {
      setVertriebsgebietValidationError(t('analysisDialog.vertriebsgebietMustSelect'));
      return;
    }
    trackAnalysis('started');
    await onSubmit?.(data);
    trackAnalysis('completed');
  };

  // Handle close attempt - show confirmation if form is dirty
  const handleCloseAttempt = () => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  // Handle confirm discard from confirmation dialog
  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  // Handle cancel discard from confirmation dialog
  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const inputClassName = cn(
    'bg-white/50 border-gray-300',
    'focus:border-gray-400 focus:ring-gray-400'
  );

  const labelClassName = 'text-[var(--color-charcoal)] font-medium';

  return (
    <>
      <GlassModal open={isOpen} onOpenChange={(open) => !open && handleCloseAttempt()}>
        <GlassModalContent
          className={cn(
            'max-w-4xl w-[calc(100%-1rem)] sm:w-[90vw]',
            'max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)]',
            'rounded-2xl sm:rounded-[var(--radius-lg)]',
            'p-0 flex flex-col gap-0',
            className
          )}
          showCloseButton={false}
          aria-describedby="analysis-dialog-description"
        >
          {/* Fixed header */}
          <div className="relative flex-shrink-0 px-4 pt-4 sm:px-6 sm:pt-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseAttempt}
              aria-label={t('common.close')}
              className="absolute right-2.5 top-2.5 sm:right-4 sm:top-4 h-8 w-8 text-[var(--color-charcoal)]/70 hover:text-[var(--color-charcoal)]"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>

            <GlassModalHeader className="pr-8">
              <GlassModalTitle>{t('analysisDialog.title')}</GlassModalTitle>
              <GlassModalDescription id="analysis-dialog-description" className="sr-only">
                {t('analysisDialog.description')}
              </GlassModalDescription>
            </GlassModalHeader>
          </div>

          {/* Scrollable form area */}
          <div className="overflow-y-auto min-h-0 flex-1 px-3 sm:px-6 text-left">
            {/* Leistungskatalog */}
            <div className="mt-2 sm:mt-3 mb-3 sm:mb-4">
              <p className="text-xs text-[var(--color-charcoal)]/70 leading-relaxed mb-3 sm:mb-4">
                {t('newAnalysis.catalogIntro')}
              </p>

              <div className="mb-3 sm:mb-4 p-2 rounded-lg bg-gradient-to-r from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10 border border-[var(--color-primary-start)]/20">
                <div className="flex items-start gap-2">
                  <Star className="h-4 w-4 text-[var(--color-primary-start)] shrink-0 mt-0.5 fill-[var(--color-primary-start)]" />
                  <span className="text-[13px] sm:text-sm font-semibold text-[var(--color-charcoal)] leading-snug">
                    {t('newAnalysis.catalogHighlight')}
                  </span>
                </div>
                <p className="text-[11px] sm:text-xs text-[var(--color-charcoal)]/55 mt-1 ml-6">
                  {t('newAnalysis.catalogAddon')}
                </p>
              </div>

              {/* Collapsible "Erfahren Sie mehr" */}
              <button
                type="button"
                onClick={() => setCatalogExpanded((prev) => !prev)}
                className="flex items-center gap-1.5 text-[13px] sm:text-sm font-medium text-[var(--color-primary-start)] hover:opacity-80 transition-opacity"
              >
                <span>{t('projectPlanDialog.learnMore')}</span>
                <ChevronDown className={cn(
                  'h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-200',
                  catalogExpanded && 'rotate-180'
                )} />
              </button>
              <div
                className={cn(
                  'grid transition-all duration-300 ease-in-out',
                  catalogExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                )}
              >
                <div className="overflow-hidden">
                  <ul className="mt-2.5 space-y-1.5 sm:space-y-2">
                    {([
                      { key: 'catalogMassnahmen', availability: 'both' as const },
                      { key: 'catalogProdukt', availability: 'both' as const },
                      { key: 'catalogKommunikation', availability: 'both' as const },
                      { key: 'catalogHandlungsempfehlungen', availability: 'both' as const },
                      { key: 'catalogVertriebsgebiet', availability: 'privatkunden' as const },
                      { key: 'catalogZielgruppeB2C', availability: 'privatkunden' as const },
                      { key: 'catalogSwot', availability: 'geschaeftskunden' as const },
                      { key: 'catalogZielgruppeB2B', availability: 'geschaeftskunden' as const },
                    ]).map(({ key, availability }) => {
                      const isBoth = availability === 'both';
                      const isActive = !watchZielgruppe
                        || isBoth
                        || watchZielgruppe === availability
                        || watchZielgruppe === 'andere';
                      const showTag = !isBoth;

                      return (
                        <li
                          key={key}
                          className={cn(
                            'flex items-start gap-2 transition-opacity duration-200',
                            !isActive && 'opacity-35'
                          )}
                        >
                          <Check className={cn(
                            'h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 mt-0.5',
                            isActive ? 'text-[var(--color-primary-start)]' : 'text-gray-300'
                          )} />
                          <span className="text-[13px] sm:text-sm text-[var(--color-charcoal)]/80 leading-snug">
                            {t(`newAnalysis.${key}`)}
                            {showTag && (
                              <span className={cn(
                                'ml-1 sm:ml-1.5 inline-block text-[9px] sm:text-[10px] font-medium px-1.5 py-px sm:py-0.5 rounded-full align-middle',
                                availability === 'privatkunden'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-amber-100 text-amber-700'
                              )}>
                                {t(`newAnalysis.catalogTag${availability === 'privatkunden' ? 'Privatkunden' : 'Geschaeftskunden'}`)}
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-[var(--glass-border)] mb-2 sm:mb-3" />

            {/* Form section headline */}
            <h3 className="text-sm sm:text-base font-semibold text-[var(--color-charcoal)] mb-3 sm:mb-4">
              {t('analysisDialog.description')}
            </h3>

            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3.5 sm:space-y-5">
              {/* Vertriebsgebiet */}
              <div className="space-y-2">
                <Label htmlFor="vertriebsgebiet-input" className={labelClassName}>
                  {t('analysisDialog.vertriebsgebiet')} <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-[var(--color-charcoal)]/60">
                  {t('analysisDialog.vertriebsgebietDescription')}
                </p>
                <div className="relative">
                  <Input
                    id="vertriebsgebiet-input"
                    type="text"
                    value={vertriebsgebietInput}
                    onChange={handleVertriebsgebietInputChange}
                    placeholder={t('analysisDialog.vertriebsgebietPlaceholder')}
                    className={cn(
                      inputClassName,
                      (errors.vertriebsgebiet || vertriebsgebietValidationError) && 'border-red-500',
                      vertriebsgebietConfirmed && 'border-green-500'
                    )}
                    aria-invalid={!!errors.vertriebsgebiet || !!vertriebsgebietValidationError}
                    aria-describedby={
                      errors.vertriebsgebiet || vertriebsgebietValidationError
                        ? 'vertriebsgebiet-error'
                        : undefined
                    }
                  />
                  {isValidatingVertriebsgebiet && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[var(--color-charcoal)]/50" />
                  )}
                  {vertriebsgebietConfirmed && !isValidatingVertriebsgebiet && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                  )}
                </div>
                {/* Hidden input for react-hook-form */}
                <input type="hidden" {...register('vertriebsgebiet')} />

                {/* Suggestions */}
                {vertriebsgebietSuggestions.length > 0 && !vertriebsgebietConfirmed && (
                  <div className="border border-[var(--glass-border)] rounded-md bg-white/80 shadow-sm overflow-hidden">
                    <p className="px-3 py-2 text-xs text-[var(--color-charcoal)]/60 border-b border-[var(--glass-border)]">
                      {t('analysisDialog.vertriebsgebietSelectHint')}
                    </p>
                    {vertriebsgebietSuggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className="w-full text-left px-3 py-3 sm:py-2 text-sm hover:bg-[var(--gradient-start)]/10 active:bg-[var(--gradient-start)]/15 transition-colors border-b last:border-b-0 border-[var(--glass-border)]"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                {errors.vertriebsgebiet && !vertriebsgebietValidationError && (
                  <p id="vertriebsgebiet-error" className="text-sm text-red-500">
                    {errors.vertriebsgebiet.message}
                  </p>
                )}
                {vertriebsgebietValidationError && (
                  <p id="vertriebsgebiet-error" className="text-sm text-red-500">
                    {vertriebsgebietValidationError}
                  </p>
                )}
              </div>

              {/* Zielgruppe */}
              <div className="space-y-2">
                <Label htmlFor="zielgruppe" className={labelClassName}>
                  {t('analysisDialog.zielgruppe')} <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-[var(--color-charcoal)]/60">
                  {t('analysisDialog.zielgruppeDescription')}
                </p>
                <Select
                  value={watchZielgruppe}
                  onValueChange={(value) =>
                    setValue('zielgruppe', value as AnalysisFormData['zielgruppe'], {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  <SelectTrigger
                    id="zielgruppe"
                    className={cn(inputClassName, errors.zielgruppe && 'border-red-500')}
                    aria-invalid={!!errors.zielgruppe}
                    aria-describedby={errors.zielgruppe ? 'zielgruppe-error' : undefined}
                  >
                    <SelectValue placeholder={t('analysisDialog.zielgruppePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="privatkunden">
                      {t('analysisDialog.zielgruppeOptions.privatkunden')}
                    </SelectItem>
                    <SelectItem value="geschaeftskunden">
                      {t('analysisDialog.zielgruppeOptions.geschaeftskunden')}
                    </SelectItem>
                    <SelectItem value="andere">
                      {t('analysisDialog.zielgruppeOptions.andere')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.zielgruppe && (
                  <p id="zielgruppe-error" className="text-sm text-red-500">
                    {errors.zielgruppe.message}
                  </p>
                )}
              </div>

              {/* Custom Zielgruppe (shown when "andere" is selected) */}
              {watchZielgruppe === 'andere' && (
                <div className="space-y-2">
                  <Label htmlFor="zielgruppeCustom" className={labelClassName}>
                    {t('analysisDialog.zielgruppeCustomLabel')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="zielgruppeCustom"
                    type="text"
                    placeholder={t('analysisDialog.zielgruppeCustomPlaceholder')}
                    className={cn(inputClassName, errors.zielgruppeCustom && 'border-red-500')}
                    aria-invalid={!!errors.zielgruppeCustom}
                    aria-describedby={errors.zielgruppeCustom ? 'zielgruppeCustom-error' : undefined}
                    {...register('zielgruppeCustom')}
                  />
                  {errors.zielgruppeCustom && (
                    <p id="zielgruppeCustom-error" className="text-sm text-red-500">
                      {errors.zielgruppeCustom.message}
                    </p>
                  )}
                </div>
              )}

              {/* Branche */}
              <div className="space-y-2">
                <Label htmlFor="branche" className={labelClassName}>
                  {t('analysisDialog.branche')} <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-[var(--color-charcoal)]/60">
                  {t('analysisDialog.brancheDescription')}
                </p>
                <Input
                  id="branche"
                  type="text"
                  placeholder={t('analysisDialog.branchePlaceholder')}
                  className={cn(inputClassName, errors.branche && 'border-red-500')}
                  aria-invalid={!!errors.branche}
                  aria-describedby={errors.branche ? 'branche-error' : undefined}
                  {...register('branche')}
                />
                {errors.branche && (
                  <p id="branche-error" className="text-sm text-red-500">
                    {errors.branche.message}
                  </p>
                )}
              </div>

              {/* Vertriebsziel */}
              <div className="space-y-2">
                <Label htmlFor="vertriebsziel" className={labelClassName}>
                  {t('analysisDialog.vertriebsziel')} <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-[var(--color-charcoal)]/60">
                  {t('analysisDialog.vertriebszielDescription')}
                </p>
                <p className="text-xs text-[var(--color-charcoal)]/50 italic">
                  {t('analysisDialog.vertriebszielOnlyHint')}
                </p>
                <Input
                  id="vertriebsziel"
                  type="text"
                  value={t('analysisDialog.vertriebszielOptions.neukunden')}
                  disabled
                  className={cn(inputClassName, 'opacity-60 cursor-not-allowed')}
                />
              </div>

              {/* Details zum Vertriebsziel (optional) */}
              <div className="space-y-2">
                <Label htmlFor="vertriebszielDetails" className={labelClassName}>
                  {t('analysisDialog.vertriebszielDetails')}
                </Label>
                <p className="text-xs text-[var(--color-charcoal)]/60">
                  {t('analysisDialog.vertriebszielDetailsDescription')}
                </p>
                <Input
                  id="vertriebszielDetails"
                  type="text"
                  placeholder={t('analysisDialog.vertriebszielDetailsPlaceholder')}
                  className={inputClassName}
                  {...register('vertriebszielDetails')}
                />
              </div>

              {/* Produktlink */}
              <div className="space-y-2">
                <Label htmlFor="produktlink" className={labelClassName}>
                  {t('analysisDialog.produktlink')} <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-[var(--color-charcoal)]/60">
                  {t('analysisDialog.produktlinkDescription')}
                </p>
                <Input
                  id="produktlink"
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  placeholder={t('analysisDialog.produktlinkPlaceholder')}
                  className={cn(inputClassName, errors.produktlink && 'border-red-500')}
                  aria-invalid={!!errors.produktlink}
                  aria-describedby={errors.produktlink ? 'produktlink-error' : undefined}
                  {...register('produktlink')}
                />
                <p className="text-xs text-[var(--color-charcoal)]/50 italic">
                  {t('analysisDialog.produktlinkTip')}
                </p>
                {errors.produktlink && (
                  <p id="produktlink-error" className="text-sm text-red-500">
                    {errors.produktlink.message}
                  </p>
                )}
              </div>

              {/* Submit button */}
              <div className="flex justify-end pt-3 sm:pt-4 pb-4 sm:pb-6 border-t border-[var(--glass-border)]">
                <Button type="submit" data-track="analysis_submit" disabled={isSubmitting} className="btn-primary w-full sm:w-auto">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {t('analysisWizard.submitting')}
                    </>
                  ) : (
                    t('newAnalysis.startAnalysis')
                  )}
                </Button>
              </div>
            </form>
          </div>
        </GlassModalContent>
      </GlassModal>

      {/* Confirmation dialog for unsaved changes */}
      <ConfirmCloseDialog
        isOpen={showConfirmClose}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}

export default AnalysisDialog;
